import { defineStore } from 'pinia'
import { ref, watch } from "vue";
import { gql } from "graphql-tag";
import { useQuery, useSubscription } from "@vue/apollo-composable";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import { EventBus } from "@/events/eventBus";
import { useHead } from "unhead";
import { useUserStore } from "@/stores/user";
import { httpStatusOkOrPending } from "@/utils/urlHelpers";


export const useWebsiteStore = defineStore('website', () => {
    const loaded = ref(false);
    const lastUpdated = ref(null);
    const website = ref(null);
    const ignoredIssues = ref({});
    const route = useRoute()
    const router = useRouter();
    const pageList = ref([]);
    const sitemapPages = ref({
        'loaded': false,
        'pages': [{
            'url': 'https://google.com',
            'path': '/x',
        }
        ]
    })

    // throttle but ensure that the last call is always executed
    function throttle(f, delay = 0, ensure = true) {
        let lastCall = Number.NEGATIVE_INFINITY;
        let wait;
        let handle;
        return (...args) => {
            wait = lastCall + delay - Date.now();
            clearTimeout(handle);
            if (wait <= 0 || ensure) {
                handle = setTimeout(() => {
                    f(...args);
                    lastCall = Date.now();
                }, wait);
            }
        };
    }

    const refetchAll = throttle(async () => {
        await refetch();
        EventBus.emit('refetchAll', null);
    }, 1100, true)

    const scanStatus = ref({
        inProgressPages: 0,
        completed: true,
    });
    const selectWebsiteVariables = ref({
        shortuuid: "",
    })
    const SELECT_LAST_UPDATED = gql`
        subscription Websites($shortuuid: String!) {
            websites_website(where: {deleted: {_eq: false}, shortuuid: {_eq:$shortuuid}}){
                id
                shortuuid
                last_updated
            }
        }`

    const {result: lastUpdatedSubscriptionResult} = useSubscription(SELECT_LAST_UPDATED, selectWebsiteVariables, () => ({
            enabled: !!selectWebsiteVariables.value.shortuuid,
        })
    )
    watch(
        lastUpdatedSubscriptionResult,
        data => {
            refetchAll()
        }
    )
    const SELECT_WEBSITE = gql`
        query Websites($shortuuid: String!) {
            websites_website(where: {deleted: {_eq: false}, shortuuid: {_eq:$shortuuid}}, order_by: {last_updated: desc}){
                id
                shortuuid
                url
                preview_favicon
                issue_stats
                last_updated
                crawl_options
                custom_dictionary
                location {
                    id
                    name
                    location_code
                    location_type
                    country_iso_code
                }
                meta
                canonical_pages(where: {skipped: {_eq: false}, redirect: {_eq: false}, deleted: {_eq: false}}, order_by: {level: asc, id: asc}, limit: 2000){
                    id
                    external_id
                    issue_stats
                    crawl_rules
                    path
                    recheck_error
                    level
                    status
                    title
                    history
                    tracked_questions_aggregate(where: {status: {_eq: "active"}}){
                        aggregate{
                            count
                        }
                    }
                    brokenlink_canonical_pages(where: {brokenlink: {ignored: {_eq: false}}}){
                        id
                        canonicalpage_id
                        brokenlink {
                            url
                            priority
                            redirect_status
                            reason
                            ignored
                            status_code
                            is_internal
                        }
                    }
                    brokenimage_canonical_pages(where: {brokenimage: {ignored: {_eq: false}}}){
                        id
                        canonicalpage_id
                        brokenimage {
                            url
                            priority
                            status
                            ignored
                        }
                    }
                    latest_page_version{
                        id
                        spellcheck_state
                        proofreading_state
                        processing_status
                        http_status_code
                        page_score
                        crawl_id
                        crawl_date
                        console_log_issues
                        broken_resource_issues
                        content_issue_missing_favicon
                        seo_issue_missing_title
                        seo_issue_missing_description
                        seo_issue_missing_image_alt_tag
                        device_renders(order_by:{device: {id:desc}}){
                            id
                            status
                            layout_errors
                            screenshots
                            broken_link_marks
                            device {
                                id
                            }
                        }
                    }
                }
                proofreadingerrors(where: {ignored: {_eq: true}}){
                    id
                }
                ignoredissues{
                    id
                    category
                    selector
                    normalized_text
                    issue
                    canonical_page{
                        id
                        external_id
                        status
                        path
                    }
                }
            }
        }`

    const {result, refetch} = useQuery(SELECT_WEBSITE, selectWebsiteVariables, () => ({
            enabled: selectWebsiteVariables.value.shortuuid !== "",
            notifyOnNetworkStatusChange: true
            // fetchPolicy: 'network-only',
        })
    )
    watch(
        result,
        async data => {
            if (!data) {
                return
            }
            if (!data.websites_website.length) {
                website.value = null;
                // await router.push({name: 'accountDashboard', query: {missing: selectWebsiteVariables.value.shortuuid}})
                return;
            }


            loaded.value = true
            let customDictionary = {}
            Object.assign(customDictionary, data.websites_website[0].custom_dictionary || {});
            if (!customDictionary.words) {
                customDictionary['words'] = [];
            }
            let issues = {};
            Object.assign(issues, data.websites_website[0].issue_stats || {});
            website.value = Object.assign({}, {
                'id': data.websites_website[0].id,
                'includePrompts': false,
                'lastUpdated': data.websites_website[0].last_updated,
                'score': Math.round(issues.score),
                'shortuuid': data.websites_website[0].shortuuid,
                'url': data.websites_website[0].url,
                'previewFavicon': data.websites_website[0].preview_favicon,
                'customDictionary': customDictionary,
                'crawlOptions': data.websites_website[0].crawl_options || {},
                'location': {
                    'id': data.websites_website[0].location.id,
                    'name': data.websites_website[0].location.name,
                    'locationCode': data.websites_website[0].location.location_code,
                    'locationType': data.websites_website[0].location.location_type,
                    'countryIsoCode': data.websites_website[0].location.country_iso_code,
                },
                'issueStats': issues,
                'meta': Object.assign({}, data.websites_website[0].meta || {}),
            });

            lastUpdated.value = data.websites_website[0].last_updated;


            const route = router.resolve({name: 'dashboard', params: {websiteId: website.value.shortuuid}})

            if (website.value.meta['image']) {
                useHead({
                    meta: [
                        {
                            name: 'twitter:card',
                            content: 'summary_large_image',
                        },
                        {
                            name: 'twitter:site',
                            content: '@pagewatchdev',
                        },
                        {
                            name: 'og:title',
                            content: website.value.meta['title'],
                        },
                        {
                            name: 'og:image',
                            content: website.value.meta['image'],
                        },
                    ],
                })
            }

            ignoredIssues.value = {
                'proofread': []
            };
            for (let issue of (data.websites_website[0].proofreadingerrors || [])) {
                ignoredIssues.value['proofread'].push(issue.id)
            }
            processPages(data.websites_website[0].canonical_pages || []);

            let inProgressPages = pageList.value.filter((p) => ['pending', 'in_progress'].includes(p.status)).length;
            scanStatus.value = {
                'completed': inProgressPages === 0,
                'inProgressPages': inProgressPages
            };

        },
        {}
    )

    function processPages(canonicalPages) {
        let resultingPages = [];
        if (canonicalPages && canonicalPages.length > 0) {
            for (let page of canonicalPages) {

                let previews = []
                let layoutErrors = []
                if (page.latest_page_version && page.latest_page_version.device_renders) {
                    for (let deviceRender of page.latest_page_version.device_renders) {
                        let screenshots = deviceRender.screenshots || {};
                        previews.push({
                            id: deviceRender.id,
                            status: deviceRender.status,
                            deviceId: deviceRender.device.id,
                            screenshot: screenshots['main'] || {},
                            screenshotFull: screenshots['full'] || {}
                        })
                        layoutErrors.push({
                            id: deviceRender.id,
                            status: deviceRender.status,
                            deviceId: deviceRender.device.id,
                            layoutErrors: deviceRender.layout_errors || []
                        })
                    }

                    previews.sort((a, b) => {
                        if (a.status === 'error') {
                            return 1
                        }
                        if (b.status === 'error') {
                            return -1
                        }

                        if (a.deviceId === 'ipad_pro_11') {
                            return -1;
                        } else if (b.deviceId === 'ipad_pro_11') {
                            return 1;
                        }
                        return 0; // Keep original order for other elements
                    });

                }

                // if (page.latest_seo_page_version) {
                let lastSlashIndex = page.path.lastIndexOf('/');

                let pathPrefix = page.path.substring(0, lastSlashIndex + 1);
                let pathEnd = page.path.substring(lastSlashIndex + 1);

                let brokenLinks = []
                if (page.brokenlink_canonical_pages) {
                    brokenLinks = page.brokenlink_canonical_pages.map(item => ({
                        url: item.brokenlink.url,
                        isInternal: item.brokenlink.is_internal,
                        priority: item.brokenlink.priority,
                        redirectStatus: item.brokenlink.redirect_status,
                        reason: item.brokenlink.reason,
                        statusCode: item.brokenlink.status_code,
                    }))
                }

                let brokenImages = []
                if (page.brokenimage_canonical_pages) {
                    brokenImages = page.brokenimage_canonical_pages.map(item => ({
                        url: item.brokenimage.url,
                        priority: item.brokenimage.priority,
                        status: item.brokenimage.status,
                    }))
                }

                let latestPageVersion = {}
                Object.assign(latestPageVersion, page.latest_page_version);
                latestPageVersion.has_broken_resources = undefined;

                let highPriority = 0;
                let mediumPriority = 0;
                if (latestPageVersion.has_broken_resources) {
                    mediumPriority++;
                }
                if (latestPageVersion.has_broken_links) {
                    highPriority++;
                }
                if (latestPageVersion.has_duplicate_title) {
                    highPriority++;
                }
                // === urls ===
                let url = website.value.url + page.path;
                // check if contains uppercase letters
                if (url.match(/[A-Z]/)) {
                    mediumPriority++;
                }
                // check if contains special characters (%, $, *, +)
                else if (url.match(/[%$*+]/)) {
                    mediumPriority++;
                }
                // ===


                // === meta_description ===
                if (latestPageVersion.has_duplicate_description) {
                    highPriority++;
                } else if (latestPageVersion.has_duplicate_meta_tags) {
                    highPriority++;
                } else if (latestPageVersion.meta_description && latestPageVersion.meta_description.length > 170) {
                    mediumPriority++;
                } else if (latestPageVersion.meta_description && latestPageVersion.meta_description.length < 80) {
                    mediumPriority++;
                }
                // ===

                if (latestPageVersion.has_duplicate_content) {
                    highPriority++;
                }
                if (latestPageVersion.has_high_loading_time) {
                    mediumPriority++;
                }
                // === heading ===
                if (latestPageVersion.has_no_h1_tag) {
                    highPriority++;
                } else if (latestPageVersion.h1_tags && latestPageVersion.h1_tags.length > 1) {
                    mediumPriority++;
                }
                // ===


                if (latestPageVersion.has_https_to_http_links) {
                    highPriority++;
                }
                if (latestPageVersion.has_duplicate_title_tags) {
                    highPriority++;
                }
                if (latestPageVersion.has_no_image_alt) {
                    mediumPriority++;
                }
                if (latestPageVersion.has_lorem_ipsum) {
                    highPriority++;
                }

                if (latestPageVersion.has_recursive_canonical || latestPageVersion.has_canonical_to_broken) {
                    highPriority++;
                } else if (!latestPageVersion.canonical_tag || latestPageVersion.has_canonical_to_redirect || latestPageVersion.has_canonical_chain) {
                    mediumPriority++;
                }

                resultingPages.push({
                    id: page.external_id,
                    status: page.status,
                    crawlDate: latestPageVersion.crawl_date,
                    crawlId: latestPageVersion.crawl_id,
                    external_id: page.external_id,
                    recheckError: page.recheck_error,
                    brokenLinks: brokenLinks,
                    brokenImages: brokenImages,
                    level: page.path === '/' ? 0 : 1,
                    url: website.value.url + page.path,
                    title: latestPageVersion.title || '',
                    metaDescription: latestPageVersion.meta_description,
                    h1Tags: latestPageVersion.h1_tags,
                    proofreadingState: latestPageVersion.proofreading_state,
                    canonicalTag: latestPageVersion.canonical_tag,
                    processingStatus: latestPageVersion.processing_status,
                    issueStats: Object.assign({}, page.issue_stats),
                    crawlRules: Object.assign({}, page.crawl_rules),
                    trackedPromptsCount: page.tracked_questions_aggregate?.aggregate?.count || 0,
                    score: Math.round(latestPageVersion.page_score),
                    history: JSON.parse(JSON.stringify(page.history)),
                    httpStatusCode: latestPageVersion.http_status_code,
                    httpStatusOkOrPending: httpStatusOkOrPending(latestPageVersion.http_status_code),
                    consoleLogIssues: Object.assign({}, latestPageVersion.console_log_issues),
                    brokenResourceIssues: Object.assign({}, latestPageVersion.broken_resource_issues),
                    contentIssueMissingFavicon: latestPageVersion.content_issue_missing_favicon,
                    seoIssueMissingTitle: latestPageVersion.seo_issue_missing_title,
                    seoIssueMissingDescription: latestPageVersion.seo_issue_missing_description,
                    seoIssueMissingImageAltTag: latestPageVersion.seo_issue_missing_image_alt_tag,

                    highPriority: highPriority,
                    mediumPriority: mediumPriority,

                    pathPrefix: pathPrefix,
                    pathEnd: pathEnd,
                    path: page.path,
                    displayPath: page.path === '/' ? '/ (Homepage)' : page.path,
                    nestLevel: (page.path.match(/\//g) || []).length,
                    devicePreviews: previews,
                    layoutErrors: layoutErrors,

                    devicePreview: previews[0] || null,
                })
            }

            // sort pageList2 by url
            resultingPages.sort((a, b) => {
                if (a.url < b.url) {
                    return -1;
                }
                if (a.url > b.url) {
                    return 1;
                }
                return 0;
            });
            // console.log(pageList2.value)

            loaded.value = true
            pageList.value = resultingPages;
        }
    }

    watch(() => route.params.websiteId, async (websiteId) => {
        if (!websiteId) {
            loaded.value = false;
            website.value = null;
        }
        selectWebsiteVariables.value.shortuuid = websiteId || ""
        setTimeout(() => {
            refetchAll();
        }, 5)
    }, {immediate: true});

    async function approveIssue(category, id, option = 'add') {

        return await axios.post(`websites/${website.value.shortuuid}/issue/`, {
            option: option,
            category: category,
            id: id
        })
    }

    async function ignoreIssue(issue, pageExternalId) {
        const userStore = useUserStore();
        try {
            return await axios.post(`websites/${website.value.shortuuid}/ignore-issue/`, {
                page_external_id: pageExternalId,
                issue: issue,
            })
        } catch (e) {
            if (e.response.status >= 400) {
                if (e.response.data.code === 'subscription') {
                    userStore.showNotification('plan')
                } else if (e.response.status === 401) {
                    userStore.showNotification('login')
                }
            }
        }
    }

    async function restoreIssue(issue, pageExternalId) {
        return await axios.post(`websites/${website.value.shortuuid}/restore-issue/`, {
            page_external_id: pageExternalId,
            issue: issue,
        })
    }

    async function addQuestion(question, status, pageExternalId) {
        const userStore = useUserStore();
        try {
            return await axios.post(`websites/${website.value.shortuuid}/page/${pageExternalId}/question`, {
                page_external_id: pageExternalId,
                status: status,
                question: question,
            })
        } catch (e) {
            if (e.response.status >= 400) {
                if (e.response.status === 422) {
                    let msg = (e.response.data?.detail?.[0]?.msg || '').replace('Value error, ', '')
                    userStore.showNotification('warning', msg, 'Validation')
                } else {
                    if (e.response.data.code === 'subscription') {
                        userStore.showNotification('plan')
                    } else if (e.response.status === 401) {
                        userStore.showNotification('login')
                    }
                }
            }
        }
    }

    async function deleteQuestion(questionId, pageExternalId) {
        const userStore = useUserStore();
        try {
            return await axios.delete(`websites/${website.value.shortuuid}/page/${pageExternalId}/question/${questionId}`)
        } catch (e) {
            if (e.response.status >= 400) {
                if (e.response.data.code === 'subscription') {
                    userStore.showNotification('plan')
                } else if (e.response.status === 401) {
                    userStore.showNotification('login')
                }
            }
        }
    }

    async function deletePage(pageExternalId) {
        const userStore = useUserStore();
        try {
            let r = await axios.delete(`websites/${website.value.shortuuid}/page/${pageExternalId}`)
            if (r.data.status === 'success') {
                //userStore.showNotification('Scan recheck initiated')
            } else {
                console.log(r)
            }
        } catch
            (e) {
            if (e.response.status >= 400) {
                if (e.response.data.code === 'subscription') {
                    userStore.showNotification('plan')
                } else if (e.response.status === 401) {
                    userStore.showNotification('login')
                }
            }
        }

    }

    async function recheckPage(pageUrl) {
        const userStore = useUserStore();
        try {
            let r = await axios.post(`websites/${website.value.shortuuid}/add-pages/`, {
                pages: [pageUrl]
            })
            if (r.data.status === 'success') {
                //userStore.showNotification('Scan recheck initiated')
            } else {
                console.log(r)
            }
        } catch
            (e) {
            if (e.response.status >= 400) {
                if (e.response.data.code === 'subscription') {
                    userStore.showNotification('plan')
                } else if (e.response.status === 401) {
                    userStore.showNotification('login')
                }
            }
        }

    }

    async function loadSitemapPages() {
        try {
            let r = await axios.get(`websites/${website.value.shortuuid}/next-pages/`)
            sitemapPages.value.pages = []
            for (let page of r.data.pages) {
                sitemapPages.value.pages.push(page)
            }

        } catch (e) {
            console.log(e)
        }
    }

    function getIssueCount(issueName, priority = '') {
        const [category, entry] = issueName.split(/\.(.+)/);
        let v = (website.value.issueStats[category] || {})[entry];
        if (priority === 'low') {
            return (v?.low || 0);
        }
        if (priority === 'medium') {
            return (v?.medium || 0);
        }
        if (priority === 'high') {
            return (v?.high || 0);
        }
        return (v?.low || 0) + (v?.medium || 0) + (v?.high || 0);
    }


    return {
        loaded,
        lastUpdated,
        scanStatus,
        website,
        ignoredIssues,
        ignoreIssue,
        restoreIssue,
        approveIssue,
        addQuestion,
        deleteQuestion,
        pageList,
        sitemapPages,
        recheckPage,
        deletePage,
        loadSitemapPages,
        getIssueCount
    }
})